/**
 * React Query Configuration
 *
 * Configures the QueryClient with optimal defaults for WiCloud:
 * - Stale time: 5 minutes (data stays fresh for 5 minutes)
 * - Cache time: 10 minutes (unused data removed after 10 minutes)
 * - Retry: 3 attempts with exponential backoff
 * - Refetch on page mount so sidebar navigation shows current data
 *
 * @see https://tanstack.com/query/latest/docs/react/reference/QueryClient
 */

import { QueryClient, DefaultOptions } from "@tanstack/react-query";

type QueryFilters = Record<string, unknown>;

function getHttpStatus(error: unknown): number | undefined {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return undefined;
  }

  const response = error.response;
  if (
    typeof response !== "object" ||
    response === null ||
    !("status" in response)
  ) {
    return undefined;
  }

  return typeof response.status === "number" ? response.status : undefined;
}

/**
 * True when the request never got an answer at all — the response interceptor rejects with the
 * raw Axios error, which carries `response` only once the server has replied.
 *
 * An ISP that blackholes traffic (FPT, 2026-09-22) produces exactly this: no status, no code the
 * browser can act on, just silence until `API_TIMEOUT`. See the retry rule below for why that
 * must not be retried.
 */
function hasNoResponse(error: unknown): boolean {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return true;
  }

  return error.response === undefined || error.response === null;
}

/**
 * Default options for all queries and mutations
 */
const queryConfig: DefaultOptions = {
  queries: {
    // Data remains fresh for 5 minutes before being considered stale
    staleTime: 5 * 60 * 1000, // 5 minutes

    // Unused data is garbage collected after 10 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)

    // Retry failed queries 3 times with exponential backoff
    retry: (failureCount, error) => {
      // A request that never reached the server is not a hiccup the next attempt can get past:
      // whatever is dropping it drops the retry too, and each attempt costs a full API_TIMEOUT
      // of silence. Three retries turned a blocked network into 90+1+90+2+90+4+90 = 367 seconds
      // of skeleton before the screen said anything at all.
      if (hasNoResponse(error)) {
        return false;
      }

      // Don't retry on 4xx errors (client errors)
      const status = getHttpStatus(error);
      if (status !== undefined && status >= 400 && status < 500) {
        return false;
      }
      // Retry up to 3 times for 5xx errors
      return failureCount < 3;
    },

    // Exponential backoff delay calculation
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

    // Don't refetch on window focus (better UX for SPA)
    refetchOnWindowFocus: false,

    // Always refetch when a page mounts. This keeps sidebar navigation current while
    // React Query can still render any cached data during the request.
    refetchOnMount: "always",

    // Refetch when network reconnects
    refetchOnReconnect: true,
  },

  mutations: {
    /**
     * Mutations are NOT retried, and that is deliberate — it used to be `retry: 1`, unfiltered.
     *
     * Two things were wrong with retrying. A 4xx is a decision, not a hiccup: the server has
     * already refused and will refuse the identical request again, so the retry only produced a
     * second rejected call and a second error toast — one click on "Nạp tiền" with a payment
     * already awaiting confirmation sent two POSTs a second apart and stacked three toasts.
     *
     * A 5xx or a dropped connection is worse. No write endpoint here takes an idempotency key, so
     * a request that reached the server, committed, and then failed on the way back is
     * indistinguishable from one that never arrived — and the retry opens a second order, a second
     * payment, or a second charge against the wallet. The customer pressing the button again is a
     * decision they can see the result of; a silent retry is not.
     *
     * A specific mutation that is genuinely idempotent can still opt in with its own `retry`.
     */
    retry: false,
  },
};

/**
 * QueryClient instance
 *
 * Singleton instance used throughout the application.
 * Wrapped by QueryClientProvider in main.tsx
 */
export const queryClient = new QueryClient({
  defaultOptions: queryConfig,
});

/**
 * Query Key Factory
 *
 * Type-safe query key generation for consistent caching.
 * Use these factories to generate query keys throughout the app.
 *
 * @example
 * ```ts
 * // User queries
 * const userQuery = useQuery({
 *   queryKey: queryKeys.users.detail(userId),
 *   queryFn: () => userService.getUser(userId)
 * });
 *
 * // Resource list with filters
 * const resourcesQuery = useQuery({
 *   queryKey: queryKeys.resources.list({ status: 'active', type: 'vm' }),
 *   queryFn: () => resourceService.getResources({ status: 'active', type: 'vm' })
 * });
 * ```
 */
export const queryKeys = {
  // Authentication
  auth: {
    all: ["auth"] as const,
    user: () => [...queryKeys.auth.all, "user"] as const,
    session: () => [...queryKeys.auth.all, "session"] as const,
  },

  // Users
  users: {
    all: ["users"] as const,
    lists: () => [...queryKeys.users.all, "list"] as const,
    list: (filters?: QueryFilters) =>
      [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    profile: () => [...queryKeys.users.all, "profile"] as const,
  },

  // Tenants
  tenants: {
    all: ["tenants"] as const,
    lists: () => [...queryKeys.tenants.all, "list"] as const,
    list: (filters?: QueryFilters) =>
      [...queryKeys.tenants.lists(), filters] as const,
    details: () => [...queryKeys.tenants.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.tenants.details(), id] as const,
    current: () => [...queryKeys.tenants.all, "current"] as const,
    members: (tenantId: string) =>
      [...queryKeys.tenants.detail(tenantId), "members"] as const,
  },

  // Resources
  resources: {
    all: ["resources"] as const,
    lists: () => [...queryKeys.resources.all, "list"] as const,
    list: (filters?: QueryFilters) =>
      [...queryKeys.resources.lists(), filters] as const,
    details: () => [...queryKeys.resources.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.resources.details(), id] as const,
    metrics: (id: string) =>
      [...queryKeys.resources.detail(id), "metrics"] as const,
    actions: (id: string) =>
      [...queryKeys.resources.detail(id), "actions"] as const,
  },

  // Billing
  billing: {
    all: ["billing"] as const,
    balance: () => [...queryKeys.billing.all, "balance"] as const,
    invoices: {
      all: () => [...queryKeys.billing.all, "invoices"] as const,
      list: (filters?: QueryFilters) =>
        [...queryKeys.billing.invoices.all(), filters] as const,
      detail: (id: string) =>
        [...queryKeys.billing.invoices.all(), id] as const,
    },
    payments: {
      all: () => [...queryKeys.billing.all, "payments"] as const,
      list: (filters?: QueryFilters) =>
        [...queryKeys.billing.payments.all(), filters] as const,
    },
    usage: (period?: string) =>
      [...queryKeys.billing.all, "usage", period] as const,
  },

  // Notifications
  notifications: {
    all: ["notifications"] as const,
    lists: () => [...queryKeys.notifications.all, "list"] as const,
    list: (filters?: QueryFilters) =>
      [...queryKeys.notifications.lists(), filters] as const,
    unreadCount: () => [...queryKeys.notifications.all, "unreadCount"] as const,
  },

  // Products & Catalog
  products: {
    all: ["products"] as const,
    lists: () => [...queryKeys.products.all, "list"] as const,
    list: (filters?: QueryFilters) =>
      [...queryKeys.products.lists(), filters] as const,
    details: () => [...queryKeys.products.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.products.details(), id] as const,
  },

  // Subscriptions
  subscriptions: {
    all: ["subscriptions"] as const,
    lists: () => [...queryKeys.subscriptions.all, "list"] as const,
    list: (filters?: QueryFilters) =>
      [...queryKeys.subscriptions.lists(), filters] as const,
    details: () => [...queryKeys.subscriptions.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.subscriptions.details(), id] as const,
    current: () => [...queryKeys.subscriptions.all, "current"] as const,
  },
} as const;

/**
 * Helper function to invalidate related queries
 *
 * @example
 * ```ts
 * // After creating a resource, invalidate resource lists
 * await queryClient.invalidateQueries({ queryKey: queryKeys.resources.lists() });
 *
 * // After updating a user, invalidate that specific user
 * await queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(userId) });
 * ```
 */

/**
 * Helper function to prefetch data
 *
 * @example
 * ```ts
 * // Prefetch user profile on app load
 * await queryClient.prefetchQuery({
 *   queryKey: queryKeys.users.profile(),
 *   queryFn: userService.getProfile
 * });
 * ```
 */
